import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Box, Typography } from "@mui/material";
import { ScheduleRounded } from "@mui/icons-material";
import color from "../../Constants/colors";
import { useMyScheduledTasks } from "../../Query/useScheduledTask";
import DashboardCard from "./DashboardCard";
import { IconTile, parseServerTime } from "./dashboardItems";
import { useDashboardColors } from "./dashboardColors";

const MAX_TASKS = 4;

const UpcomingTasksCard: React.FC = () => {
  const { t } = useTranslation("home");
  const navigate = useNavigate();
  const { accentSoft, accentText } = useDashboardColors();
  const { data: tasks = [] } = useMyScheduledTasks();
  // Fixed when the card mounts; the home page is short-lived enough.
  const [now] = useState(() => Date.now());

  const upcoming = tasks
    .map((task) => ({ task, nextRun: parseServerTime(task.next_run_at) }))
    .filter(
      (entry): entry is { task: typeof entry.task; nextRun: Date } =>
        entry.task.enabled && !!entry.nextRun && entry.nextRun.getTime() > now,
    )
    .sort((a, b) => a.nextRun.getTime() - b.nextRun.getTime())
    .slice(0, MAX_TASKS);

  // Nothing scheduled is the common case; the card would only be noise.
  if (upcoming.length === 0) return null;

  const inTime = (date: Date) => {
    const minutes = Math.round((date.getTime() - now) / 60000);
    if (minutes < 1) return t("hub.upcoming.soon");
    if (minutes < 60) return t("hub.upcoming.inMinutes", { count: minutes });
    const hours = Math.round(minutes / 60);
    if (hours < 48) return t("hub.upcoming.inHours", { count: hours });
    return t("hub.upcoming.inDays", { count: Math.round(hours / 24) });
  };

  return (
    <DashboardCard
      title={t("hub.upcoming.title")}
      subtitle={t("hub.upcoming.subtitle")}
    >
      <Box component="ul" sx={{ listStyle: "none", m: 0, p: 0, mx: -1 }}>
        {upcoming.map(({ task, nextRun }) => {
          const canOpen = task.conversation_id !== null;
          return (
            <Box component="li" key={task.id}>
              <Box
                component={canOpen ? "button" : "div"}
                type={canOpen ? "button" : undefined}
                onClick={
                  canOpen
                    ? () => navigate(`/como-experts/${task.conversation_id}`)
                    : undefined
                }
                sx={{
                  width: "100%",
                  display: "flex",
                  alignItems: "center",
                  gap: 1.5,
                  px: 1,
                  py: 1,
                  border: 0,
                  borderRadius: "10px",
                  background: "none",
                  textAlign: "left",
                  fontFamily: "inherit",
                  cursor: canOpen ? "pointer" : "default",
                  "&:hover": canOpen
                    ? { backgroundColor: accentSoft }
                    : undefined,
                }}
              >
                <IconTile tone="accent">
                  <ScheduleRounded sx={{ fontSize: 20 }} />
                </IconTile>
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Typography
                    noWrap
                    sx={{
                      fontSize: 14,
                      lineHeight: "20px",
                      fontWeight: 600,
                      color: color.gray900,
                    }}
                  >
                    {task.name}
                  </Typography>
                  <Typography
                    noWrap
                    sx={{
                      fontSize: 12,
                      lineHeight: "16px",
                      color: color.gray500,
                    }}
                  >
                    {task.agent_name ?? task.task_name}
                  </Typography>
                </Box>
                <Typography
                  title={nextRun.toLocaleString()}
                  sx={{
                    flexShrink: 0,
                    fontSize: 12,
                    lineHeight: "16px",
                    fontWeight: 600,
                    color: accentText,
                  }}
                >
                  {inTime(nextRun)}
                </Typography>
              </Box>
            </Box>
          );
        })}
      </Box>
    </DashboardCard>
  );
};

export default UpcomingTasksCard;
